import React, {useState} from 'react';
import {connect} from "react-redux";
import {RootState} from "../../../../../reducers";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {Trans, useTranslation, withTranslation} from "react-i18next";
import PopupModal from "../../../Modal/PopupModal";
import {getCart} from "../../../../services/Cart/reducer";
import ItemHelper from "../../../../services/Menu/services/Item/ItemHelper";
import DisabledCartItem from "./DisabledCartItem";
import {ReactComponent as AlertIco} from "../../../../../images/icons/alert-error.svg";
import {OrderType} from "../../../../services/Api/types";
import {DateHelper} from "../../../../services/Common/Date/DateHelper";
import {useCart} from "../../../../services/Cart/cartService";
import Analytics from "../../../../services/Common/Analytics/Analytics";
import DateService from "../../../../services/Common/Date/DateService";
import AppConfig from "../../../../services/AppConfig";
import IframeWidget from "../../../../utils/IframeWidget";

interface OwnProps {
    show: boolean;
    onHide: () => void;
    items: any[];
    type: "ORDER_TYPE" | "PICKUP_AT";
    orderType?: OrderType;
    pickupAt?: string;
    onSubmit: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const DisabledCartItemsModal = ({show, onHide, cart, items, type, orderType, pickupAt, locale, onSubmit}: Props) => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const cartService = useCart();
    const [cartItems] = useState(cart.items);

    const getDescription = () => {
        if (type === "ORDER_TYPE") {
            return <Trans
                i18nKey="modules.cart.field.not_available_items_for_order_type.title"
                values={{type: t(`enums.order.type.${orderType}`)}}
                components={{strong: <strong className={`fw-normal ${AppConfig.isDarkMode() ? "" : "strong-dark"}`}/>}}
            />
        }

        return <Trans
            i18nKey="modules.cart.field.not_available_items_for_pickup_at.title"
            values={{date: DateHelper.getFormattedNearestDate(pickupAt ?? DateHelper.format(DateService.now()), t, locale ?? "", true)}}
            components={{strong: <strong className={`fw-normal ${AppConfig.isDarkMode() ? "" : "strong-dark"}`}/>}}
        />
    }

    const handleRemoveCartItem = async (item: any) => {
        const cartItem = cart.items.find((cartItem: any) => cartItem.item_id === item.id);

        await cartService.updateItem(cartItem.id, {
            ...cartItem,
            quantity: 0
        });
        await Analytics.removeFromCart(cartItem);
    }

    const handleSubmit = async () => {
        setLoading(true);
        try {
            for (const item of items) {
                await handleRemoveCartItem(item);
            }
            onSubmit();
            IframeWidget.closeModal();
        } catch (err) {
            setLoading(false);
        }
    }

    return (
        <PopupModal
            className="disabled-cart-items-modal"
            title={t("modules.cart.field.not_available_items.title")}
            description={getDescription()}
            show={show} onHide={onHide}
            headerIcon={<AlertIco/>}
            headerIconClassName="text-danger"
            bodyContent={
                <div className="pt-2 mb-4 d-flex flex-column gap-3">
                    {
                        items.map((item: any) => {
                                const cartItem = cartItems.find((cartItem: any) => cartItem.item_id === item.id);
                                const promotion = ItemHelper.getItemPromotion(cartItem, cart.promotions);

                                return (
                                    <DisabledCartItem key={item.id} cartItem={cartItem} item={item} promotion={promotion}/>
                                )
                            }
                        )
                    }
                </div>
            }
            primaryButton={
                <button type="button" className={`btn btn-spinner btn-default w-100 ${loading ? "loading" : ""}`}
                        onClick={handleSubmit} disabled={loading}>
                    <span className="left spinner-border spinner-border-sm"/>
                    {t("modules.cart.action.remove_disabled_items.title")}
                </button>
            }
            secondaryButton={
                <button type="button" className="btn btn-default" data-dismiss="modal"
                        onClick={() => IframeWidget.onHide(onHide)}>
                    {t("common.action.cancel")}
                </button>
            }
        />
    )
}

const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
    session: getSession(state.session),
    cart: getCart(state.cart),
    locale: selectLocale(state.session),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(DisabledCartItemsModal));
